import { ScrollWrapper } from "@/components/scroll-wrapper"
import { CommandSeparator } from "@/components/ui/command"
import { FreeTextMedicationCreation } from "@/modules/patients/detail/medication/components/free-text-medication-creation"
import { DeMedicationDrawer } from "@/modules/patients/detail/medication/de/de-medication-drawer"
import { type MedicationSearchResultsType } from "@/modules/patients/detail/medication/de/search-results"
import { deMedicationFragmentInline$data } from "@/modules/patients/detail/medication/de/services/__generated__/deMedicationFragmentInline.graphql"
import { useDeMedicationContext } from "@/modules/patients/detail/medication/store/useDeMedicationStore"
import { MEDICATION_MODES } from "@/modules/patients/detail/medication/types/medication-mode"
import { RecommendedMedicationOptions } from "@/modules/patients/detail/recommended-medications/components/recommended-medication-options"
import {
  DeMedicationResult,
  DeRecommendedMedicationsSearchResult,
  DeSubstanceResult,
} from "@/modules/patients/detail/recommended-medications/de/recommended-medications-search-result"
import { useCreateRecommendedMedication } from "@/modules/patients/detail/recommended-medications/services/useCreateRecommendedMedication"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface BaseRecommendedMedicationResults {
  progressNoteId: string
  searchResultStatus?: "default" | "noResults" | "hasResults"
  inputValue: string
  renderOptions?: { title: string; options: deMedicationFragmentInline$data[] }[]
  onCloseMenu: VoidFunction
}

interface RecommendedMedicationResultProps extends BaseRecommendedMedicationResults {
  preparations: { type: "DE"; preparations?: DeMedicationResult }
  substances: { type: "DE"; substances?: DeSubstanceResult }
}

export const RecommendedMedicationResult = (props: RecommendedMedicationResultProps) => {
  const { progressNoteId, searchResultStatus, inputValue, preparations, renderOptions, substances, onCloseMenu } = props

  const country = useCountryParam()
  const patientId = usePatientParam()
  const openDrawerWithoutConfirmation = useDrawerStore((state) => state.openDrawerWithoutConfirmation)
  const setDeMedicationDrawerMode = useDeMedicationContext((state) => state.setDeMedicationDrawerMode)
  const createRecommendedMedication = useCreateRecommendedMedication(country)

  const handleSelectOption = (option: deMedicationFragmentInline$data) => {
    if (patientId) {
      handleCreateRecommendedMedication(option.id)
    }
  }

  const handleCreateRecommendedMedication = (medicationId: string) => {
    createRecommendedMedication({
      medicationId,
      patientId,
      progressNoteId,
      onCompleted: onCloseMenu,
      onError: onCloseMenu,
    })
  }

  const handleOpenAddMedicalDrawer = (selectedItem: MedicationSearchResultsType) => {
    openDrawerWithoutConfirmation(
      () => (
        <DeMedicationDrawer
          recommendedMedication={{ ...selectedItem, progressNoteId, onAddSuccess: handleCreateRecommendedMedication }}
        />
      ),
      "MedicationDrawer",
      { onBeforeOpen: () => setDeMedicationDrawerMode(MEDICATION_MODES.CREATION) },
    )
    onCloseMenu()
  }

  const handleSelectSearchItem = (selectedItem: MedicationSearchResultsType) => {
    handleOpenAddMedicalDrawer(selectedItem)
  }

  const handleFreeTextMedicationSelection = () => {
    handleOpenAddMedicalDrawer({ freeTextMedicationName: inputValue })
  }

  return (
    <ScrollWrapper className="max-h-80 py-2">
      {searchResultStatus === "noResults" && (
        <FreeTextMedicationCreation
          className="mt-4 px-2"
          searchTerm={inputValue}
          onClick={handleFreeTextMedicationSelection}
        />
      )}
      {searchResultStatus === "hasResults" && (
        <>
          <RecommendedMedicationOptions
            handleSelectOption={handleSelectOption}
            inputValue={inputValue}
            hasSearchResults
            renderOptions={renderOptions}
          />
          {(preparations.preparations || substances.substances) && <CommandSeparator className="mb-0" alwaysRender />}
          <DeRecommendedMedicationsSearchResult
            searchText={inputValue}
            preparations={preparations.preparations}
            substances={substances.substances}
            handleSelectSearchItem={handleSelectSearchItem}
          />
        </>
      )}
    </ScrollWrapper>
  )
}
